import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { brandAssets } from '../../data/mock';
import MobileWrapper from '../../components/layout/MobileWrapper';
import AppleMusicConnectModal from '../../components/shared/AppleMusicConnectModal';
import { useMusicKit } from '../../context/MusicKitContext';
import './AuthPage.css';

const AuthPage = ({ onAuthSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  // Start with 'welcome' step (Music Movement Moments video screen)
  const [step, setStep] = useState('welcome'); // 'welcome', 'phone', 'code'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']); // 6 digits for OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mockCode, setMockCode] = useState('');
  const [resendTimer, setResendTimer] = useState(30); // 30 second countdown
  const [canResend, setCanResend] = useState(false);
  const [showAppleMusicModal, setShowAppleMusicModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  const otpRefs = useRef([]);
  const { sendPhoneCode, verifyPhoneCode } = useAuth();
  const { isAuthorized, authorize } = useMusicKit();

  // Handle navigation state (e.g., coming back from onboarding)
  useEffect(() => {
    if (location.state?.step) {
      setStep(location.state.step);
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Countdown timer for resend OTP
  useEffect(() => {
    let interval;
    if (step === 'code' && resendTimer > 0 && !canResend) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer, canResend]);

  // Reset timer when entering code step
  useEffect(() => {
    if (step === 'code') {
      setResendTimer(30);
      setCanResend(false);
    }
  }, [step]);

  // Handle video looping for welcome screen - OPTIMIZED for mobile
  useEffect(() => {
    if (step === 'welcome' && videoRef.current) {
      const video = videoRef.current;
      video.load();
      video.play().catch(err => console.log('Video autoplay blocked - user interaction required'));

      // REMOVED timeupdate listener - it fires 4+ times per second and kills mobile performance
      // Instead, just use the native loop attribute and ended event as backup
      const handleEnded = () => {
        video.currentTime = 0;
        video.play().catch(() => {});
      };

      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [step]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 10);
    
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const getRawPhoneNumber = (formatted) => {
    return '+1' + formatted.replace(/\D/g, '');
  };

  // BYPASS OTP - Direct login with phone number only
  const handleDirectLogin = async (e) => {
    if (e) e.preventDefault();
    const raw = getRawPhoneNumber(phoneNumber);
    
    if (raw.length < 12) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('[Auth] Direct login (bypassing OTP) for:', raw);
      // Use the universal test code "123456" to verify directly
      const userData = await verifyPhoneCode(raw, '123456');
      console.log('[Auth] Direct login successful:', userData);
      
      // Navigate to appropriate destination using React Router
      if (userData.needs_onboarding) {
        navigate('/onboarding', { replace: true });
      } else {
        // Check for stored deep link path
        const redirectPath = localStorage.getItem('tapInRedirectAfterAuth');
        let targetPath = '/app';
        if (redirectPath) {
          localStorage.removeItem('tapInRedirectAfterAuth');
          console.log('[Auth] Deep linking to:', redirectPath);
          targetPath = redirectPath;
        }
        
        // Set a flag to show Apple Music modal after navigation
        // The modal will be shown in the app layout/dashboard
        console.log('[Auth] Setting Apple Music modal flag in localStorage');
        localStorage.setItem('tapInShowAppleMusicModal', 'true');
        // Clear any previous modal shown state to ensure it shows
        sessionStorage.removeItem('tapInAppleMusicModalShown');
        console.log('[Auth] Flag set, navigating to:', targetPath);
        navigate(targetPath, { replace: true });
      }
    } catch (err) {
      console.error('[Auth] Direct login error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Login failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle Apple Music connection
  const handleAppleMusicConnect = async () => {
    try {
      await authorize();
      console.log('[Auth] Apple Music connected successfully');
      // Navigate to the pending destination
      if (pendingNavigation) {
        navigate(pendingNavigation, { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      console.error('[Auth] Apple Music connection failed:', err);
      // Still navigate even if Apple Music fails - they can connect later
      if (pendingNavigation) {
        navigate(pendingNavigation, { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    }
  };

  // Handle skipping Apple Music (user doesn't have it)
  const handleAppleMusicSkip = () => {
    console.log('[Auth] User skipped Apple Music');
    setShowAppleMusicModal(false);
    // Navigate to the pending destination
    if (pendingNavigation) {
      navigate(pendingNavigation, { replace: true });
    } else {
      navigate('/app', { replace: true });
    }
  };

  // Legacy OTP flow (keeping for reference but not used)
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    // Now just calls direct login
    await handleDirectLogin(e);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newOtp = [...otp];
      digits.split('').forEach((d, i) => {
        if (i + index < 6) newOtp[i + index] = d;
      });
      setOtp(newOtp);
      const lastIndex = Math.min(index + digits.length - 1, 5);
      otpRefs.current[lastIndex]?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };


  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    
    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userData = await verifyPhoneCode(getRawPhoneNumber(phoneNumber), code);
      // After successful verification, navigate using React Router
      if (userData?.is_onboarded) {
        navigate('/app', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes('No account found')) {
        // New user - go to onboarding
        navigate('/onboarding', { replace: true });
      } else {
        setError(detail || 'Invalid verification code');
        setLoading(false);
      }
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    setLoading(true);
    setError('');
    
    try {
      const result = await sendPhoneCode(getRawPhoneNumber(phoneNumber));
      if (result.mock_code) {
        setMockCode(result.mock_code);
      }
      setResendTimer(30);
      setCanResend(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Music Movement Moments - Video background with Login/Sign up buttons
  if (step === 'welcome') {
    return (
      <>
        <div className="auth-video-page" data-testid="auth-welcome">
          {/* Video Background - New onboarding video */}
          <video
            ref={videoRef}
            className="auth-video-background"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="https://customer-assets.emergentagent.com/job_music-tapin/artifacts/dhvgkhxs_Tap%20In%20Onboarding%20Screen%201%20.mp4" type="video/mp4" />
          </video>

          {/* Dark overlay for text readability */}
          <div className="auth-video-overlay"></div>

          {/* Content */}
          <div className="auth-video-content">
            {/* Pulsing Tap In Logo */}
            <div className="auth-logo-container">
              <div className="auth-logo-pulse-ring"></div>
              <div className="auth-logo-pulse-ring-2"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_jamtracks/artifacts/yd7sls6l_1.png"
                alt="Tap In"
                className="auth-logo-image"
              />
          </div>

          {/* Centered Tagline */}
          <div className="auth-tagline-centered">
            <h1 className="auth-tagline-text">Music.</h1>
            <h1 className="auth-tagline-text">Movement.</h1>
            <h1 className="auth-tagline-text">Moments.</h1>
          </div>

          {/* Buttons - Mobile Optimized */}
          <div className="auth-buttons">
            <button 
              className="auth-login-button"
              onClick={(e) => {
                e.preventDefault();
                console.log('[Auth] Login clicked');
                setStep('phone');
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[Auth] Login touched');
                setStep('phone');
              }}
              style={{ 
                touchAction: 'manipulation', 
                WebkitTapHighlightColor: 'transparent',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                minHeight: '56px'
              }}
              data-testid="welcome-login-btn"
            >
              Login
            </button>
            <button 
              className="auth-signup-button"
              onClick={(e) => {
                e.preventDefault();
                console.log('[Auth] Signup clicked');
                setStep('phone');
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[Auth] Signup touched');
                setStep('phone');
              }}
              style={{ 
                touchAction: 'manipulation', 
                WebkitTapHighlightColor: 'transparent',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                minHeight: '56px'
              }}
              data-testid="welcome-signup-btn"
            >
              Sign up
            </button>
          </div>
        </div>

        {/* Back button */}
        <button
          className="auth-back-button"
          onClick={() => navigate('/')}
          data-testid="back-to-landing"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>
      
      {/* Apple Music Connect Modal */}
      {showAppleMusicModal && (
        <AppleMusicConnectModal
          onConnect={handleAppleMusicConnect}
          onSkip={handleAppleMusicSkip}
          onClose={handleAppleMusicSkip}
          isMandatory={false}
        />
      )}
      </>
    );
  }

  // Step 2: Phone number entry - Simplified for better mobile performance
  if (step === 'phone') {
    return (
      <>
      <MobileWrapper>
        <div className="min-h-screen bg-white relative overflow-hidden" data-testid="auth-phone">
          {/* Back Button */}
          <button
            onClick={() => setStep('welcome')}
            className="absolute top-4 left-4 z-20 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center"
            style={{ touchAction: 'manipulation' }}
            data-testid="back-to-welcome"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>

          {/* Simple gradient background - no blur effects */}
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20" />

          {/* Main content container */}
          <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-8">
            
            {/* Logo - simplified, no heavy animations */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 -m-3 rounded-full bg-blue-100/50" />
                <img 
                  src={brandAssets.logo} 
                  alt="Tap In" 
                  className="w-14 h-14 object-contain relative z-10"
                />
              </div>
            </div>

            {/* Form Container */}
            <div className="w-full max-w-md flex flex-col items-center">
              <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
                Log In to Tap In
              </h1>
              <p className="text-gray-500 text-center text-sm mb-6">
                Please provide your phone number
              </p>

              {error && (
                <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm text-center">
                  {error}
                </div>
              )}

              {/* Simplified Form */}
              <form 
                onSubmit={handleDirectLogin} 
                className="w-full flex flex-col gap-5"
              >
                {/* Phone Input */}
                <div className="w-full">
                  <div className="relative w-full">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-600">
                      <span className="text-lg">🇺🇸</span>
                      <span className="font-medium text-gray-500">+1</span>
                      <div className="w-px h-5 bg-gray-300 ml-1" />
                    </div>
                    <input
                      type="tel"
                      placeholder="(555) 555-5555"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                      className="w-full pl-28 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-400"
                      style={{ fontSize: '18px', minHeight: '60px' }}
                      inputMode="tel"
                      autoComplete="tel"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                {/* Sign In Button - Simplified event handling */}
                <button
                  type="submit"
                  disabled={loading || phoneNumber.replace(/\D/g, '').length < 10}
                  className="w-full rounded-2xl bg-[#516ffe] text-white font-bold text-xl flex items-center justify-center disabled:opacity-50"
                  style={{ 
                    touchAction: 'manipulation',
                    minHeight: '64px',
                    padding: '18px 24px'
                  }}
                  data-testid="bypass-login-btn"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'SIGN IN'
                  )}
                </button>
                
                <p className="text-center text-gray-400 text-sm mt-2 w-full">
                  No verification code needed
                </p>
              </form>
            </div>
          </div>
        </div>
      </MobileWrapper>
      
      {/* Apple Music Connect Modal */}
      {showAppleMusicModal && (
        <AppleMusicConnectModal
          onConnect={handleAppleMusicConnect}
          onSkip={handleAppleMusicSkip}
          onClose={handleAppleMusicSkip}
          isMandatory={false}
        />
      )}
      </>
    );
  }

  // Step 3: OTP verification - Clean modern design centered on screen
  return (
    <>
    <MobileWrapper>
      <div className="h-screen bg-white relative overflow-hidden flex flex-col" data-testid="auth-code">
        {/* Back Button */}
        <button
          onClick={() => {
            setStep('phone');
            setOtp(['', '', '', '', '', '']);
            setError('');
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            setStep('phone');
            setOtp(['', '', '', '', '', '']);
            setError('');
          }}
          className="absolute top-4 left-4 z-20 w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          style={{ touchAction: 'manipulation', minWidth: '48px', minHeight: '48px' }}
          data-testid="back-to-phone"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>

        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/30 via-white to-purple-50/20" />

        <div className="relative z-10 px-6 flex-1 flex flex-col justify-center">
          {/* Logo at top center with BLUE pulse */}
          <div className="flex justify-center mb-10">
            <div className="relative">
              {/* Blue pulse rings */}
              <div className="absolute inset-0 -m-4 rounded-full bg-blue-500/20 animate-ping" style={{ animationDuration: '2s' }} />
              <div className="absolute inset-0 -m-3 rounded-full bg-blue-500/15 animate-pulse" />
              <div className="absolute inset-0 -m-2 rounded-full bg-blue-100/50 blur-xl" />
              <img 
                src={brandAssets.logo} 
                alt="Tap In" 
                className="w-16 h-16 object-contain relative z-10"
                style={{ filter: 'drop-shadow(0 0 15px rgba(81, 111, 254, 0.5))' }}
              />
            </div>
          </div>

          {/* Form - centered */}
          <div className="max-w-sm mx-auto w-full">
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Enter OTP
            </h1>
            <p className="text-gray-500 text-center mb-2">
              We&apos;ve sent a code to {getRawPhoneNumber(phoneNumber)}
            </p>
            
            {/* Change number link */}
            <button
              onClick={() => {
                setStep('phone');
                setOtp(['', '', '', '', '', '']);
                setError('');
              }}
              className="text-[#516ffe] text-center mb-6 underline font-medium block mx-auto"
            >
              Change number
            </button>
            
            {/* Mock code display for testing */}
            {mockCode && (
              <p className="text-[#516ffe] font-mono text-sm mb-4 bg-blue-50 px-3 py-2 rounded-lg text-center">
                Test code: {mockCode}
              </p>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleVerifyCode} className="space-y-6">
              {/* 6 OTP input boxes */}
              <div className="flex justify-center gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-11 h-13 text-center text-xl font-bold bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                    data-testid={`otp-input-${index}`}
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={loading || otp.some(d => !d)}
                className="w-full py-4 rounded-full bg-[#516ffe] text-white font-semibold text-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-blue-500/30 min-h-[56px]"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Verify'
                )}
              </button>
            </form>

            {/* Resend OTP with timer */}
            <div className="mt-6 text-center">
              {canResend ? (
                <button
                  onClick={handleResendOtp}
                  onTouchEnd={(e) => { e.preventDefault(); if (!loading) handleResendOtp(); }}
                  disabled={loading}
                  className="text-[#516ffe] font-medium min-h-[44px] px-4"
                  style={{ touchAction: 'manipulation' }}
                >
                  Resend OTP
                </button>
              ) : (
                <p className="text-gray-500">
                  Resend OTP in{' '}
                  <span className="font-mono text-gray-700">
                    00:{resendTimer.toString().padStart(2, '0')}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </MobileWrapper>
    
    {/* Apple Music Connect Modal */}
    {showAppleMusicModal && (
      <AppleMusicConnectModal
        onConnect={handleAppleMusicConnect}
        onSkip={handleAppleMusicSkip}
        onClose={handleAppleMusicSkip}
        isMandatory={false}
      />
    )}
    </>
  );
};

export default AuthPage;
